/**
 * 用户 / 登录状态
 *
 * 职责与移动端一致：登录信息、积分、经销商信息、个人资料修改。
 * 桌面/Web 端没有微信，统一走手机号 + 验证码。
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiAuth, type FrontLoginInfo, type FrontUserInfo } from '@/api/sys/login'
import { apiAppContext } from '@/api/sys/context'
import {
  getUserInfo,
  updateUserNickName,
  updateUserAvatar,
  updateUserGender,
  updateUserEmail,
  updateUserMobile,
  sendSmsCode,
  updateUserPassword,
  hasPassword,
  parseFileUrl
} from '@/api/sys/user'
import { getUserPoints } from '@/api/sys/point'
import { getToken, setToken, clearToken } from '@/api/xajax'
import { clientType } from '@/utils/client'

export const useUserStore = defineStore('user', () => {
  const token = ref(getToken())
  /** 登录信息 */
  const loginInfo = ref<FrontLoginInfo | null>(null)
  /** 用户信息 */
  const userInfo = ref<FrontUserInfo>({})
  /** 积分 */
  const points = ref(0)
  /** 今日是否已签到（来自 /context/init 的 todaySign，首页签到按钮用） */
  const todaySign = ref(false)
  /** 经销商 id */
  const dealerId = ref('')
  /** 是否已初始化（避免重复拉取） */
  const initialized = ref(false)

  const isLogin = computed(() => !!token.value)
  const nickName = computed(() => userInfo.value?.nickName || '')

  /**
   * 顶栏 / 个人中心展示名：已登录但没设昵称的新用户用脱敏手机号兜底，
   * 不能显示「未登录」（与移动端 mine.vue 的 displayName 逻辑一致）。
   */
  const displayName = computed(() => {
    if (!token.value) return '未登录'
    const n = userInfo.value?.nickName
    if (n) return n
    const m = userInfo.value?.mobile
    if (m) return m.length === 11 ? `${m.slice(0, 3)}****${m.slice(7)}` : m
    const l = userInfo.value?.loginName
    if (l) return l
    return '已登录'
  })
  /** 头像：统一走 parseFileUrl（相对路径拼 OSS + https 页面升级，避免各处重复实现） */
  const avatar = computed(() => parseFileUrl(userInfo.value?.avatar || ''))
  const pointsText = computed(() => String(points.value ?? 0))

  /** 落盘 token */
  function setLogin(info: FrontLoginInfo) {
    loginInfo.value = info
    token.value = info?.accessToken || ''
    setToken(token.value)
    if (info?.userInfo) userInfo.value = info.userInfo
  }

  function clearLogin() {
    loginInfo.value = null
    userInfo.value = {}
    token.value = ''
    points.value = 0
    todaySign.value = false
    dealerId.value = ''
    initialized.value = false
    clearToken()
  }

  /**
   * 手机号 + 验证码登录（未注册自动创建）
   * @param mobile 手机号
   * @param validCode 短信验证码
   * @param inviteCode 邀请码（可选）
   */
  async function mobileLogin(mobile: string, validCode: string, inviteCode?: string) {
    // type 统一由 src/utils/client.ts 判定（Web 端 web / 桌面版 app），不再写死
    const res = await apiAuth.mobileLogin({ mobile, validCode, inviteCode, type: clientType() })
    if (res.code !== 0 || !res.data?.accessToken) throw new Error(res.message || '登录失败')
    setLogin(res.data)
    return res.data
  }

  /**
   * 手机号 / 登录名 + 密码登录
   *
   * 后端会判断 name 是否为手机号（^1[3-9]\d{9}$），是则按手机号查，否则按登录名查。
   * 密码需先在个人中心「登录密码」设置过（或在移动端设置）才能用此方式登录。
   */
  async function loginByName(name: string, cipher: string) {
    const res = await apiAuth.login({ name, cipher, client: clientType() })
    if (res.code !== 0 || !res.data?.accessToken) throw new Error(res.message || '登录失败')
    setLogin(res.data)
    return res.data
  }

  /** 拉取登录用户信息：优先 /context/init（一次拿全），失败降级 /user/info */
  async function loadUserInfo(force = false) {
    if (!token.value) return
    if (initialized.value && !force) return
    try {
      const res = await apiAppContext.getContext()
      if (res.code === 0 && res.data) {
        const ctx = res.data
        // 未登录（token 失效）时后端仍返回 200 + isLogin=false，此时不要拿空 user 覆盖
        if (ctx.isLogin === false) {
          userInfo.value = {}
          return
        }
        if (ctx.user) userInfo.value = { ...userInfo.value, ...ctx.user }
        if (ctx.user?.dealerId) dealerId.value = ctx.user.dealerId
        todaySign.value = !!ctx.todaySign
      } else {
        const r2 = await getUserInfo()
        if (r2.code === 0 && r2.data) userInfo.value = { ...userInfo.value, ...r2.data }
      }
      const p = await getUserPoints()
      if (p.code === 0 && p.data) points.value = p.data.usableValue ?? p.data.totalValue ?? 0
      else if (userInfo.value && (userInfo.value as any).points !== undefined) points.value = (userInfo.value as any).points
      initialized.value = true
    } catch {
      /* ignore */
    }
  }

  /** 刷新积分 */
  async function refreshPoints() {
    if (!token.value) return
    try {
      const p = await getUserPoints()
      if (p.code === 0 && p.data) points.value = p.data.usableValue ?? p.data.totalValue ?? 0
    } catch {
      /* ignore */
    }
  }

  async function updateNickName(name: string) {
    const res = await updateUserNickName({ nickName: name })
    if (res.code !== 0) throw new Error(res.message || '修改失败')
    userInfo.value.nickName = name
  }

  async function updateAvatar(url: string) {
    const res = await updateUserAvatar({ avatar: url })
    if (res.code !== 0) throw new Error(res.message || '修改失败')
    userInfo.value.avatar = url
  }

  async function updateGender(gender: string) {
    const res = await updateUserGender({ gender })
    if (res.code !== 0) throw new Error(res.message || '修改失败')
    userInfo.value.gender = gender
  }

  async function updateEmail(email: string) {
    const res = await updateUserEmail({ email })
    if (res.code !== 0) throw new Error(res.message || '修改失败')
    userInfo.value.email = email
  }

  /** 修改手机号：先 sendSms 拿验证码，再带 code 提交 */
  async function sendMobileCode(mobile?: string) {
    const res = await sendSmsCode(mobile)
    if (res.code !== 0) throw new Error(res.message || '验证码发送失败')
  }

  async function updateMobile(mobile: string, code: string) {
    const res = await updateUserMobile({ mobile, code })
    if (res.code !== 0) throw new Error(res.message || '修改失败')
    userInfo.value.mobile = mobile
  }

  async function updatePassword(code: string, cipher: string) {
    const res = await updateUserPassword({ code, cipher })
    if (res.code !== 0) throw new Error(res.message || '修改失败')
  }

  /** 是否已设置密码（决定「设置密码 / 修改密码」文案） */
  async function checkHasPassword() {
    const res = await hasPassword()
    return res.code === 0 ? !!res.data : false
  }

  /** 资料改完后统一刷新一次，保证顶栏与页面拿到的是服务端最新值 */
  async function reload() {
    await loadUserInfo(true)
  }

  /** 初始化：有 token 就拉一次用户信息 */
  async function init() {
    if (!token.value) return
    await loadUserInfo()
  }

  return {
    token,
    loginInfo,
    userInfo,
    points,
    todaySign,
    dealerId,
    initialized,
    isLogin,
    nickName,
    displayName,
    avatar,
    pointsText,
    setLogin,
    clearLogin,
    mobileLogin,
    loginByName,
    loadUserInfo,
    refreshPoints,
    updateNickName,
    updateAvatar,
    updateGender,
    updateEmail,
    sendMobileCode,
    updateMobile,
    updatePassword,
    checkHasPassword,
    reload,
    init
  }
})
